import jsPDF from "jspdf";
import data from "../data/data.json";
import leaf from "../assets/blatt.png";
import star from "../assets/stern.png";
import coffee from "../assets/coffee.png";

export async function createStandplanPDF({ highlightStands = [], selectedCategories = [], lang = "de" }) {
  const pdf = new jsPDF("p", "mm", "a4");

  const PAGE_WIDTH = pdf.internal.pageSize.getWidth();
  const PAGE_HEIGHT = pdf.internal.pageSize.getHeight();
  const MARGIN = 8;
  const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;

  // === Daten vorbereiten: Treffer herausfiltern und sortieren ===
  const matches = data.stands
    .map((stand) => {
      const company = stand.company?.[lang] ?? stand.company?.de ?? "";
      const category = stand.category?.[lang] ?? stand.category?.de ?? "";
      const product = stand.product?.[lang] ?? stand.product?.de ?? "";
      const description = stand.description?.[lang] ?? stand.description?.de ?? "";

      const categories = category.split(",").map(c => c.trim().toLowerCase());

      const isHighlight = highlightStands.some(h => h.id === stand.id);
      const isCategoryMatch = selectedCategories.some(c => categories.includes(c.toLowerCase()));

      return { ...stand, company, category, product, description, isMatch: isHighlight || isCategoryMatch };
    })
    .filter(s => s.isMatch)
    .sort((a, b) => a.label.localeCompare(b.label, "de", { numeric: true }));

  // === Seite 1: Standplan + Trefferliste ===
  drawPage1(pdf, matches, lang, MARGIN, CONTENT_WIDTH);

  // === Seite 2+: Herstellerübersicht ===
  await drawManufacturers(pdf, matches, lang, MARGIN, CONTENT_WIDTH, PAGE_HEIGHT, PAGE_WIDTH);

  // === PDF exportieren ===
  const pdfBlob = pdf.output("blob");
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.onloadend = async () => {
      try {
        const response = await fetch("/api/save-pdf", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pdfBase64: reader.result }),
        });
        const result = await response.json();
        resolve(result.url);
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsDataURL(pdfBlob);
  });
}

/* ==================== Hilfsfunktionen ==================== */

function drawPage1(pdf, matches, lang, MARGIN, CONTENT_WIDTH) {
  let y = MARGIN;

  // Titel
  pdf.setFontSize(18);
  pdf.setTextColor(0);
  pdf.text(lang === "de" ? "Standplan – Trefferübersicht" : "Standplan – Matches", MARGIN, y);
  y += 10;

  // === Standplan auf PDF-Breite skalieren ===
  const allX = data.stands.flatMap(s => [s.x, s.x + s.width]);
  const allY = data.stands.flatMap(s => [s.y, s.y + s.height]);
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);

  const planWidth = maxX - minX;
  const planHeight = maxY - minY;

  const scale = CONTENT_WIDTH / planWidth; // volle PDF-Breite
  const STANDPLAN_HEIGHT = planHeight * scale;

  // Rechtecke + Labels
  data.stands.forEach(stand => {
    const isMatch = matches.some(m => m.id === stand.id);

    if (isMatch) pdf.setFillColor(22, 163, 74);
    else pdf.setFillColor(251, 113, 133);

    pdf.setDrawColor(0);
    const x = MARGIN + (stand.x - minX) * scale;
    const yPos = y + (stand.y - minY) * scale;
    const width = stand.width * scale;
    const height = stand.height * scale;

    pdf.rect(x, yPos, width, height, "F");

    pdf.setFontSize(7);
    pdf.setTextColor(0);
    pdf.text(stand.label, x + width / 2, yPos + height / 2, { align: "center", baseline: "middle" });
  });

  y += STANDPLAN_HEIGHT + 5;

  // Trefferliste in 2 Spalten mit Zeilenumbruch, aufsteigend sortiert
  pdf.setFontSize(14);
  pdf.text(lang === "de" ? "Treffer" : "Matches", MARGIN, y);
  y += 6;

  pdf.setFontSize(11);
  if (matches.length === 0) {
    pdf.setTextColor(120);
    pdf.text(lang === "de" ? "Keine Treffer vorhanden" : "No matches available", MARGIN, y);
  } else {
    // Treffer aufsteigend nach Label sortieren
    const sortedMatches = [...matches].sort((a, b) => a.label.localeCompare(b.label, "de", { numeric: true }));

    const colGap = 10; // Abstand zwischen Spalten
    const colWidth = (CONTENT_WIDTH - colGap) / 2;
    let yCol1 = y;
    let yCol2 = y;
    const lineHeight = 6;

    sortedMatches.forEach((m, i) => {
      const text = `ITA ${m.label} – ${m.company}`;
      const lines = pdf.splitTextToSize(text, colWidth);

      if (i % 2 === 0) {
        lines.forEach(line => {
          pdf.text(line, MARGIN, yCol1);
          yCol1 += lineHeight;
        });
      } else {
        lines.forEach(line => {
          pdf.text(line, MARGIN + colWidth + colGap, yCol2);
          yCol2 += lineHeight;
        });
      }
    });
  }
}


/* ==================== Bild-Helper ==================== */
function getImageDimensions(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = src;
  });
}

async function drawManufacturers(pdf, matches, lang, MARGIN, PAGE_WIDTH, PAGE_HEIGHT) {
  // pdf.addPage();
  let y = MARGIN;
  const blockSpacing = 5;

  const isMobile = window.innerWidth < 768;
  const CONTENT_WIDTH = isMobile ? PAGE_WIDTH - 10 : PAGE_WIDTH - 2 * MARGIN;

  // const titleFontSize = isMobile ? 32 : 16;
  const standFontSize = isMobile ? 24 : 12;
  const nameFontSize = isMobile ? 22 : 14;
  const descFontSize = isMobile ? 20 : 12;

  const lineHeight = 6;
  const emojiSize = 5;
  const maxPhotoWidth = isMobile ? 60 : 40;

  // pdf.setFontSize(titleFontSize);
  // pdf.text(lang === "de" ? "Herstellerübersicht" : "Manufacturers", MARGIN, y);
  // y += 10;

  const emojis = {
    "🌿": leaf,
    "⭐️": star,
    "☕": coffee,
  };

  for (const m of matches) {
    pdf.addPage();
    let y = MARGIN;
    // --- FOTO ---
    if (m.photoPng) {
      try {
        const imgDim = await getImageDimensions(m.photoPng);
        const autoHeight = (imgDim.height / imgDim.width) * maxPhotoWidth;

        if (y + autoHeight > PAGE_HEIGHT - MARGIN) {
          pdf.addPage();
          y = MARGIN;
        }

        pdf.addImage(m.photoPng, "PNG", MARGIN, y, maxPhotoWidth, autoHeight);
        y += autoHeight + blockSpacing;
      } catch (err) {
        console.warn("Bild konnte nicht geladen werden:", err);
      }
    }
    // --- Herstellername schwarz ---
    if (m.company) {
      pdf.setTextColor(0);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(nameFontSize);
      const companyLines = pdf.splitTextToSize(m.company, CONTENT_WIDTH);
      companyLines.forEach(line => {
        if (y + lineHeight > PAGE_HEIGHT - MARGIN) {
          pdf.addPage();
          y = MARGIN;
        }
        pdf.text(line, MARGIN, y);
        y += lineHeight;
      });
      y += 2;
    }

    if (m.label) {
      // --- Standnummer grün ---
      pdf.setFontSize(standFontSize);
      pdf.setTextColor(56, 161, 105); // nur Label grün

      if (y + lineHeight > PAGE_HEIGHT - MARGIN) {
        pdf.addPage();
        y = MARGIN;
      }

      pdf.text(`${m.label}`, MARGIN, y);

      // --- Abstand zum nächsten Datensatz ---
      y += lineHeight * 2; // 2 Zeilen Abstand
    }

    
    pdf.setTextColor(0); // zurück zu Schwarz
    // --- Textblöcke ---
    const textBlocks = [
      { text: m.companyLegal },
      { text: `${m.street || ""} ${m.streetNumber || ""}` },
      { text: `${m.postalcode || ""} ${m.city || ""}` },
      { text: m.region },
      { spacer: true },
      { label: lang === "de" ? "Kontakt" : "Contact:", text: m.contact },
      { label: lang === "de" ? "Telefon" : "Phone", text: m.phone },
      { label: lang === "de" ? "E-Mail" : "Email", text: m.email },
      { label: lang === "de" ? "Internet" : "Website", text: m.web },
      { spacer: true },
      { label: lang === "de" ? "Kategorie:" : "Category:", text: m.category },
      { label: lang === "de" ? "Produkte:" : "Products:", text: m.product }
    ];

    textBlocks.forEach(block => {
      if (block.spacer) {
        y += lineHeight;
        return;
      }

      if (!block.text) return;

      // Seitenumbruch prüfen
      if (y + lineHeight > PAGE_HEIGHT - MARGIN) {
        pdf.addPage();
        y = MARGIN;
      }

      let x = MARGIN;

      // --- LABEL fett ---
      if (block.label) {
        pdf.setFont("helvetica", "bold");
        pdf.text(block.label + " ", x, y);
        x += pdf.getTextWidth(block.label + " ");
      }

      // --- TEXT normal ---
      pdf.setFont("helvetica", "normal");
      const lines = pdf.splitTextToSize(block.text, CONTENT_WIDTH - (x - MARGIN));

      lines.forEach((line, i) => {
        if (i > 0) {
          y += lineHeight;
          x = MARGIN; // Zeilenumbruch → links anfangen
        }
        pdf.text(line, x, y);
      });

      y += lineHeight + blockSpacing / 2;
    });

    // --- Beschreibung mit Emoji-Unterstützung (Blocksatz, letzte Zeile linksbündig) ---
    if (m.description) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(descFontSize);
      pdf.setTextColor(0);

      const profileLabel = lang === "de" ? "Profil:" : "Profile:";

      if (y + lineHeight > PAGE_HEIGHT - MARGIN) {
        pdf.addPage();
        y = MARGIN;
      }

      pdf.text(profileLabel, MARGIN, y);
      y += lineHeight;

      pdf.setFont("helvetica", "normal");

      let cursorX = MARGIN;
      let cursorY = y;

      const emojiSize = 5;
      const blockSpacing = 2;
      const maxLineWidth = PAGE_WIDTH - 2 * MARGIN;

      // Emoji-Bilder laden
      const emojisLoaded = {};
      for (const [key, src] of Object.entries({ "🌿": leaf, "⭐": star, "☕": coffee })) {
        const img = new Image();
        img.src = src;
        emojisLoaded[key] = img;
        await new Promise(res => { img.onload = res });
      }

      // Text + Emojis splitten
      const cleanDescription = m.description.replace(/\uFE0F/g, ""); // Stern ohne VS16
      const parts = cleanDescription.split(/(🌿|⭐|☕)/g);

      let lineWords = [];
      let lineWidth = 0;

      const flushLine = (isLastLine = false) => {
        if (lineWords.length === 0) return;

        let x = MARGIN;

        if (!isLastLine && lineWords.length > 1) {
          // Blocksatz nur bei >1 Wort
          const extraSpace = (maxLineWidth - lineWidth) / (lineWords.length - 1);
          lineWords.forEach((w, idx) => {
            if (emojisLoaded[w]) {
              pdf.addImage(emojisLoaded[w], "PNG", x, cursorY - emojiSize + 1, emojiSize, emojiSize);
              x += emojiSize + extraSpace;
            } else {
              pdf.text(w, x, cursorY);
              x += pdf.getTextWidth(w) + extraSpace;
            }
          });
        } else {
          // letzte Zeile oder nur 1 Wort → linksbündig
          lineWords.forEach(w => {
            if (emojisLoaded[w]) {
              pdf.addImage(emojisLoaded[w], "PNG", x, cursorY - emojiSize + 1, emojiSize, emojiSize);
              x += emojiSize;
            } else {
              pdf.text(w, x, cursorY);
              x += pdf.getTextWidth(w);
            }
          });
        }

        cursorY += lineHeight;
        lineWords = [];
        lineWidth = 0;

        if (cursorY > PAGE_HEIGHT - MARGIN) {
          pdf.addPage();
          cursorY = MARGIN;
        }
      };

      for (const part of parts) {
        if (emojisLoaded[part]) {
          if (lineWidth + emojiSize > maxLineWidth) flushLine();
          lineWords.push(part);
          lineWidth += emojiSize;
        } else if (part) {
          const words = part.split(" ");
          for (const word of words) {
            const wordWithSpace = word + " ";
            const wordWidth = pdf.getTextWidth(wordWithSpace);
            if (lineWidth + wordWidth > maxLineWidth) flushLine();
            lineWords.push(wordWithSpace);
            lineWidth += wordWidth;
          }
        }
      }

      // letzte Zeile linksbündig
      flushLine(true);

      y = cursorY + blockSpacing;
    }
  }
}

